// @file For linkings on tldraw canvas

import { getEditorObject } from '@@/bits/tldraw/editor'
import { getHostname, isUrl } from '@@/bits/url'
import { vDel, vSet } from '@@/bits/vue'
import type { WhiteboardLinkingLink } from '@@/bits/whiteboard_linking'
import { defineStore } from 'pinia'
import { ref } from 'vue'

export const useWhiteboardLinkingStore = defineStore('whiteboardLinking', () => {
  // Links
  const linkings = ref<Record<string, WhiteboardLinkingLink>>({})

  async function upsertLinking(shapeId: string, linking: WhiteboardLinkingLink): Promise<void> {
    vSet(linkings.value, shapeId, linking)
  }

  async function deleteLinking(shapeId: string): Promise<void> {
    if (linkings.value[shapeId] != null) {
      vDel(linkings.value, shapeId)
    }
  }

  function checkIfLinkingExists(shapeId: string): boolean {
    return linkings.value[shapeId]?.target != null
  }

  function getLinking(shapeId: string): WhiteboardLinkingLink | null {
    return linkings.value[shapeId]
  }

  function getLinkingText(shapeId: string): string {
    const linking = getLinking(shapeId)
    const editor = getEditorObject()
    if (editor == null) return ''
    const readableLinkingString = linking?.toReadableString(editor.getPages()) ?? ''
    return isUrl(readableLinkingString)
      ? getHostname(readableLinkingString).replace(/^www\./, '')
      : readableLinkingString
  }

  function linkError(shapeId: string): string {
    const linking = getLinking(shapeId)
    const editor = getEditorObject()
    if (editor == null) return ''
    return linking?.errorMessage(editor) ?? ''
  }

  function triggerLink(shapeId): void {
    if (linkError(shapeId) !== '') return
    const editor = getEditorObject()
    if (editor == null) return
    const linking = getLinking(shapeId)
    linking?.handle(editor)
  }

  // Panel

  const xLinkingPanel = ref(false)
  const xLinkingPanelShapeId = ref<string | null>(null)

  function openLinkingPanel(shapeId: string): void {
    xLinkingPanel.value = true
    xLinkingPanelShapeId.value = shapeId
  }
  function closeLinkingPanel(): void {
    xLinkingPanel.value = false
    xLinkingPanelShapeId.value = null
  }

  return {
    // Links
    // State
    linkings,
    // Actions
    upsertLinking,
    deleteLinking,
    checkIfLinkingExists,
    getLinking,
    getLinkingText,
    triggerLink,
    linkError,

    // Panel
    // State
    xLinkingPanel,
    xLinkingPanelShapeId,
    // Actions
    openLinkingPanel,
    closeLinkingPanel,
  }
})
